import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';

import { STORAGE_KEYS, THEME } from '@constants/index';
import { useLocalStorage } from '@hooks/useLocalStorage';
import type { Theme } from '@app-types/index';

interface ThemeApi {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeApi | null>(null);

const evaluateDark = (mode: Theme): boolean =>
  mode === THEME.dark ||
  (mode === THEME.system && window.matchMedia('(prefers-color-scheme: dark)').matches);

const applyTheme = (mode: Theme): void => {
  document.documentElement.classList.toggle('dark', evaluateDark(mode));
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useLocalStorage<Theme>(STORAGE_KEYS.theme, THEME.system);

  useEffect(() => {
    applyTheme(theme);
    if (theme !== THEME.system) return undefined;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyTheme(THEME.system);
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, [theme]);

  const isDark = evaluateDark(theme);

  const value = useMemo<ThemeApi>(
    () => ({
      theme,
      setTheme,
      isDark,
      toggleTheme: () => setTheme(isDark ? THEME.light : THEME.dark),
    }),
    [theme, setTheme, isDark],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeApi {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a <ThemeProvider />');
  return ctx;
}
